import React, { useEffect, useState } from 'react';
import { Save, User, Lock, Phone, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/api';

export const ProfileSettings: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ firstName: '', lastName: '', phone: '' });
  const [pwd, setPwd] = useState({ currentPassword: '', password: '', confirm: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pwdMsg, setPwdMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) setProfile({ firstName: user.firstName, lastName: user.lastName, phone: user.phone ?? '' });
  }, [user]);

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true); setProfileMsg(null);
    try {
      await apiClient.updateProfile({ firstName: profile.firstName, lastName: profile.lastName, phone: profile.phone });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error ?? 'Failed to update profile.' });
    } finally { setProfileSaving(false); }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd.password !== pwd.confirm) { setPwdMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    if (pwd.password.length < 8) { setPwdMsg({ type: 'error', text: 'Password must be at least 8 characters.' }); return; }
    setPwdSaving(true); setPwdMsg(null);
    try {
      await apiClient.updateProfile({ currentPassword: pwd.currentPassword, password: pwd.password });
      setPwdMsg({ type: 'success', text: 'Password changed successfully.' });
      setPwd({ currentPassword: '', password: '', confirm: '' });
    } catch (err: any) {
      setPwdMsg({ type: 'error', text: err.response?.data?.error ?? 'Failed to change password.' });
    } finally { setPwdSaving(false); }
  };

  const Alert = ({ msg }: { msg: { type: string; text: string } }) => (
    <div style={{
      background: msg.type === 'success' ? '#dcfce7' : '#fee2e2',
      color: msg.type === 'success' ? '#166534' : '#991b1b',
      padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14,
    }}>{msg.text}</div>
  );

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Account Settings</h1>
      </div>

      {/* Account info banner */}
      <div className="card" style={{ marginBottom: 20, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 700, flexShrink: 0 }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{user?.firstName} {user?.lastName}</div>
            <div style={{ fontSize: 13, color: '#64748b', display: 'flex', gap: 16, marginTop: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={12} />{user?.email}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User size={12} />{user?.role?.replace(/_/g, ' ')}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Profile form */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <User size={18} color="#3b82f6" />
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>Personal Information</h2>
          </div>
          {profileMsg && <Alert msg={profileMsg} />}
          <form onSubmit={saveProfile}>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>First Name *</label>
              <input required value={profile.firstName} onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Last Name *</label>
              <input required value={profile.lastName} onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label><Phone size={12} style={{ display: 'inline', marginRight: 4 }} />Phone</label>
              <input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+250..." />
            </div>
            <button type="submit" className="btn btn-primary" disabled={profileSaving}>
              <Save size={14} /> {profileSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Password form */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <Lock size={18} color="#6366f1" />
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>Change Password</h2>
          </div>
          {pwdMsg && <Alert msg={pwdMsg} />}
          <form onSubmit={savePassword}>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Current Password *</label>
              <input type="password" required value={pwd.currentPassword} onChange={e => setPwd(p => ({ ...p, currentPassword: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>New Password *</label>
              <input type="password" required value={pwd.password} onChange={e => setPwd(p => ({ ...p, password: e.target.value }))} placeholder="Min 8 characters" />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Confirm New Password *</label>
              <input type="password" required value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ background: '#6366f1' }} disabled={pwdSaving}>
              <Lock size={14} /> {pwdSaving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
